// useAgentBaseRecommend — fires /api/agentbase-recommend when the
// broker clicks Recommend on the Quote page. Tracks sync state so
// QuoteDeliveryV4's action row can render "Synced ✓ · Open in
// AgentBase" inline.
//
// State machine:
//   idle      — no recommendation made yet (or after clearing)
//   syncing   — POST in flight
//   synced    — 2xx + ok=true response
//   retrying  — first attempt failed; auto-retry running
//   error     — final error after retry exhausted
//
// The broker NEVER sees a blocking spinner. Sync runs alongside the
// rest of the workflow; failure shows a subtle red status line with a
// manual retry button — the recommendation itself (in the local
// session store) is unaffected.

import { useCallback, useRef, useState } from 'react';
import type { Plan } from '@/types/plans';
import type { Client, Medication, Provider } from '@/types/session';
import type { ScoredPlan, PlanBrainResult } from '@/hooks/usePlanBrain';

export type RecommendSyncState = 'idle' | 'syncing' | 'synced' | 'retrying' | 'error';

export interface RecommendSyncResult {
  client_id: string;
  agentbase_url: string;
  created: boolean;
  webhook_forwarded: boolean;
  webhook_error: string | null;
  giveback_flagged: boolean;
}

// Exported so the SaveSessionButton's belt-and-suspenders structured
// sync can read a snapshot of the same input shape from the
// useAgentBaseSyncSnapshot store. Same payload either path.
export interface SyncInput {
  client: Client;
  sessionId: string;
  startedAt: number;
  brokerNpn: string;
  brokerId: string;
  recommendedPlan: Plan;
  recommendedScored: ScoredPlan;
  medications: Medication[];
  providers: Provider[];
  brainResult: PlanBrainResult;
  /** Per-recommended-plan medication context (tier + monthly cost +
   *  PA/ST flags). Caller derives this from the medRows array on the
   *  recommended column. Name/dose/form are parsed from the RxNorm
   *  display string ("gabapentin · 300 MG · Oral Capsule") so the CRM
   *  client_medications row gets a clean ingredient name in the name
   *  column instead of the full display string. */
  medContext: Array<{
    name: string;
    rxcui: string | null;
    /** Dose from session.Medication.dose when present, else parsed
     *  from segment 2 of the RxNorm display string (e.g. "10 MG"). */
    dose: string | null;
    /** Dose form parsed from segment 3 of the RxNorm display
     *  ("Oral Capsule", "Pen Injector"). May be multi-segment
     *  ("Oral Tablet · Extended Release"). */
    form: string | null;
    /** Free-text dosing instructions (e.g. "1 tablet daily"). */
    frequency: string | null;
    /** "30" or "90" — derived from the broker's pharmacy-fill toggle.
     *  Lands in client_medications.refill_days. */
    refill_days: string | null;
    /** Quantity per fill and AgentBase providers.id of the pharmacy —
     *  set on scanned bottles; fill a blank on the CRM row only. */
    quantity?: string | null;
    pharmacy_id?: number | null;
    tier_on_recommended_plan: number | null;
    monthly_cost: number | null;
    /** PA/ST flags — only set when the caller has real per-drug
     *  per-plan brain output. Optional because the agent-v3 path
     *  fires Recommend before the brain produces paStFlags; sending
     *  hardcoded `false` there was misleading. The agentbase-recommend
     *  endpoint treats absent === unknown. */
    pa_required?: boolean;
    st_required?: boolean;
    /** Drug-identity + Part D/B classification fields AgentBase ingests
     *  under these exact names (don't rename). Each is optional and
     *  may be null when the source the med carries doesn't fill it:
     *   - dose_form: dosage form ("Oral Tablet"), mirrors `form`.
     *   - strength: numeric strength ("10 MG"), mirrors `dose`.
     *   - strength_display: human-readable strength+form; null unless
     *     the med was hydrated with it (drug-search doesn't carry it).
     *   - package_size / package_unit / quantity_unit: packaging facts;
     *     not available on the agent-v3 Medication today → null.
     *   - not_part_d: true when the drug is excluded from Part D (OTC).
     *   - part_b_status: 'part_b' | 'part_b_or_d' | 'not_part_d' | null
     *     — the Part D/B classification; null when unknown. */
    dose_form?: string | null;
    strength?: string | null;
    strength_display?: string | null;
    package_size?: string | null;
    package_unit?: string | null;
    quantity_unit?: string | null;
    not_part_d?: boolean | null;
    part_b_status?: 'part_b' | 'part_b_or_d' | 'not_part_d' | null;
  }>;
  /** Provider-network status on the recommended plan. */
  providerContext: Array<{
    name: string;
    npi: string;
    specialty: string | null;
    network_status: 'in' | 'out' | 'unknown';
  }>;
  /** Optional AgentBase clients.id — when set, the recommend endpoint
   *  bypasses the phone/last_name+dob match and resolves straight to
   *  this row. Agent-v3 hydrates this from the ?clientId= deep-link. */
  agentbaseClientId?: number | null;
  /** Optional CMS compliance snapshot. Captured on the Compliance
   *  screen + the Intake call-recording disclosure. Flows through to
   *  clients.soa_confirmed_at / call_recording_disclosed_at and a row
   *  in planmatch_activity_log. Null on the fire-and-forget call from
   *  CompareScreen — compliance hasn't been collected yet at that
   *  point in the flow. */
  compliance?: {
    soaConfirmed: boolean;
    soaConfirmedAt: string | null;
    scopeConfirmed: boolean;
    moopExplained: boolean;
    formularyExplained: boolean;
    networkExplained: boolean;
    consentRecorded: boolean;
    consentRecordedAt: string | null;
    callRecordingDisclosed: boolean;
    callRecordingDisclosedAt: string | null;
  } | null;
  /** Optional session metadata logged alongside the activity row. */
  sessionSummary?: {
    zip: string;
    county: string;
    planYear: number;
    brainArchetype?: string;
    estimatedAnnualCost?: number;
  } | null;
  /** Optional CRM health-profile snapshot. Populated on syncs that
   *  fire after the broker completes the AgentBase Health Intake — the
   *  caller passes the CRM's client_health_profiles row (fetched via
   *  /api/client-health-profile) so the /api/agentbase-recommend
   *  endpoint can upsert it back into the CRM Supabase's
   *  client_health_profiles table with synced_to_planmatch_at set. */
  healthContext?: {
    conditions: string[];
    family_history: Record<string, string[]>;
    lifestyle: Record<string, unknown>;
    utilization: Record<string, unknown>;
    complexity_scores: Record<string, number>;
    risk_flags: Array<{
      level: string;
      cat: string;
      text: string;
      actions: string[];
    }>;
  } | null;
}

const RETRY_DELAY_MS = 2_000;

export function useAgentBaseRecommend() {
  const [state, setState] = useState<RecommendSyncState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecommendSyncResult | null>(null);
  // Track which planId was last synced so a re-click on Recommend
  // (different plan) re-fires; idempotent re-click on the same plan
  // doesn't.
  const syncedPlanIdRef = useRef<string | null>(null);

  const buildBody = useCallback((input: SyncInput) => {
    const todayIso = new Date().toISOString();
    return {
      client: {
        name: input.client.name,
        phone: input.client.phone,
        dob: input.client.dob,
        zip: input.client.zip,
        county: input.client.county,
        state: input.client.state,
        plan_type: input.client.planType,
        medicaid_confirmed: input.client.medicaidConfirmed,
        email: null,
      },
      recommended_plan: {
        contract_id: input.recommendedPlan.contract_id,
        plan_id: input.recommendedPlan.plan_number,
        // Plan.id has the canonical "H1036-308-0" form; the third
        // dash-separated segment is the segment_id we send to AgentBase.
        segment_id: (input.recommendedPlan.id.split('-')[2] ?? '0'),
        plan_name: input.recommendedPlan.plan_name,
        carrier: input.recommendedPlan.carrier,
        star_rating: input.recommendedPlan.star_rating,
        premium: input.recommendedPlan.premium,
        moop: input.recommendedPlan.moop_in_network,
        rx_deductible: input.recommendedPlan.drug_deductible,
        part_b_giveback: input.recommendedPlan.part_b_giveback,
        ribbon: input.recommendedScored.ribbon,
      },
      medications: input.medContext,
      providers: input.providerContext,
      brain_snapshot: {
        detected_conditions: input.brainResult.detectedConditions.map((d) => ({
          condition: d.condition,
          confidence: d.confidence,
        })),
        client_archetype: input.brainResult.archetype.archetype,
        archetype_label: input.brainResult.archetype.label,
        medication_patterns: input.brainResult.medicationPatterns.map((p) => ({
          id: p.id,
          severity: p.severity,
          summary: p.summary,
        })),
        applied_broker_rules: input.recommendedScored.appliedRules.map((r) => ({
          rule_id: r.ruleId,
          action: r.action,
          points: r.points,
          reason: r.reason,
        })),
        red_flags: input.recommendedScored.redFlags.map((f) => ({
          id: f.id,
          severity: f.severity,
          message: f.message,
        })),
        // True when the plan year's Part D formulary isn't published yet: the
        // cost breakdown below EXCLUDES drugs, so net_annual is not a complete
        // figure. Persist the flag so the CRM record is self-describing and
        // nothing downstream reads net_annual as a finished total.
        drug_costs_unavailable: input.recommendedScored.drugCostsUnavailable === true,
        // Don't persist a drug-excluding breakdown as if it were the real
        // annual cost — withhold it entirely until the formulary publishes.
        real_annual_cost:
          input.recommendedScored.realAnnualCost && !input.recommendedScored.drugCostsUnavailable
            ? {
                premium: input.recommendedScored.realAnnualCost.premium,
                drugs: input.recommendedScored.realAnnualCost.drugs,
                medical_visits: input.recommendedScored.realAnnualCost.medicalVisits,
                supplies: input.recommendedScored.realAnnualCost.supplies,
                er_expected: input.recommendedScored.realAnnualCost.erExpected,
                hospital_expected: input.recommendedScored.realAnnualCost.hospitalExpected,
                giveback_savings: input.recommendedScored.realAnnualCost.givebackSavings,
                net_annual: input.recommendedScored.realAnnualCost.netAnnual,
              }
            : null,
        composite_score: input.recommendedScored.composite,
        weights: input.brainResult.weights,
        finalists_compared: input.brainResult.scored.map((s) => ({
          contract_id: s.plan.contract_id,
          plan_name: s.plan.plan_name,
          composite: s.composite,
        })),
      },
      session: {
        session_token: input.sessionId,
        started_at: new Date(input.startedAt).toISOString(),
        quote_date: todayIso,
        broker_id: input.brokerId,
        broker_npn: input.brokerNpn,
      },
      // Giveback flag drives AgentBase's AEP "Needs Attention" surface.
      // True when the recommended plan has a non-zero Part B giveback —
      // the broker should re-evaluate at next AEP because giveback
      // amounts shift year-over-year.
      giveback_plan_enrolled: (input.recommendedPlan.part_b_giveback ?? 0) > 0,
      // Optional pass-throughs — endpoint treats both as optional and
      // (when present) writes recommended_*/soa_confirmed_at/
      // call_recording_disclosed_at columns + a planmatch_activity_log
      // row. Absent on the fire-and-forget Compare-screen call.
      client_id: input.agentbaseClientId ?? undefined,
      compliance: input.compliance ?? undefined,
      session_summary: input.sessionSummary ?? undefined,
      health_profile: input.healthContext ?? undefined,
    };
  }, []);

  const postOnce = useCallback(async (body: unknown) => {
    // Debug log — full payload visibility for the meds/providers sync
    // investigation. Logged just before fetch so the broker (or
    // whoever's tailing the browser console) can see exactly what's
    // sent for any given Recommend click. Pulls counts up front so
    // the line is greppable even when the body itself collapses.
    if (typeof console !== 'undefined') {
      const b = body as {
        medications?: unknown[];
        providers?: unknown[];
        recommended_plan?: { plan_name?: string };
      };
      console.log(
        `[agentbase-recommend] POST body — meds=${b.medications?.length ?? 0} providers=${b.providers?.length ?? 0} plan="${b.recommended_plan?.plan_name ?? '?'}"`,
        body,
      );
    }
    const r = await fetch('/api/agentbase-recommend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = (await r.json().catch(() => ({}))) as
      | (RecommendSyncResult & { ok: boolean })
      | { ok: false; error: string };
    if (!r.ok || (json as { ok: boolean }).ok === false) {
      const msg = 'error' in json && typeof json.error === 'string'
        ? json.error
        : `agentbase-recommend ${r.status}`;
      throw new Error(msg);
    }
    return json as RecommendSyncResult & { ok: true };
  }, []);

  const sync = useCallback(async (
    input: SyncInput,
  ): Promise<{ ok: true; result: RecommendSyncResult } | { ok: false; error: string }> => {
    const planId = input.recommendedPlan.id;
    // Idempotent same-plan re-click — skip the network round trip but
    // still return the previously-cached success so callers awaiting
    // (e.g. EnrollScreen's save-then-open flow) don't stall.
    if (syncedPlanIdRef.current === planId && state === 'synced' && result) {
      return { ok: true, result };
    }

    setState('syncing');
    setError(null);
    const body = buildBody(input);

    // Convert a raw handler response into a success/failure decision.
    // The handler returns top-level ok:true even when the webhook forward
    // to planmatch_sessions fails (best-effort), because the direct
    // clients-table write succeeded — but a webhook failure means the
    // ⚡ approval queue never fires and the enrollment is only
    // half-landed. Surface it as a failure with an explicit "half-landed"
    // message so callers (SlotCell EnrollCardButton, QuoteDeliveryV4
    // SyncStatusPill) can show the broker exactly what happened. This
    // is the fix for the silent-webhook-failure bug documented in the
    // Aug 9 smoke report.
    const decide = (
      r: RecommendSyncResult,
    ): { ok: true; result: RecommendSyncResult } | { ok: false; error: string } => {
      if (r.webhook_forwarded === false) {
        const msg =
          `Client record updated, but the AgentBase queue forward failed: ${r.webhook_error ?? 'unknown error'}`;
        return { ok: false, error: msg };
      }
      return { ok: true, result: r };
    };

    try {
      const r = await postOnce(body);
      const outcome = decide(r);
      if (outcome.ok) {
        setResult(r);
        setState('synced');
        syncedPlanIdRef.current = planId;
      } else {
        // Half-landed: direct write succeeded so we DO stash the result
        // (so the pill can still show the CRM URL / giveback badge),
        // but leave syncedPlanIdRef.current unset so a retry click
        // actually re-fires — otherwise the same-plan short-circuit
        // above would swallow the retry.
        setResult(r);
        setError(outcome.error);
        setState('error');
      }
      return outcome;
    } catch (err1) {
      console.warn('[agentbase-recommend] first attempt failed:', (err1 as Error).message);
      setState('retrying');
      await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
      try {
        const r = await postOnce(body);
        const outcome = decide(r);
        if (outcome.ok) {
          setResult(r);
          setState('synced');
          syncedPlanIdRef.current = planId;
        } else {
          setResult(r);
          setError(outcome.error);
          setState('error');
        }
        return outcome;
      } catch (err2) {
        const msg = (err2 as Error).message;
        console.error('[agentbase-recommend] retry failed:', msg);
        setError(msg);
        setState('error');
        return { ok: false, error: msg };
      }
    }
  }, [buildBody, postOnce, state, result]);

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setResult(null);
    syncedPlanIdRef.current = null;
  }, []);

  return { state, error, result, sync, reset };
}
